/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useCallback, useEffect, useState } from "react";
import {
  ResourcesSortInput,
  useGetAllResourceDetailsLazyQuery,
  ResourcesSearchInput,
  OsType,
} from "~/generated/graphql";
import { ResourceDetails } from "../../types";

type Props = {
  sort?: ResourcesSortInput;
  input: ResourcesSearchInput;
};

const LIMIT = 100;

export function useGetAllResourceDetails({ sort, input }: Props) {
  const [allResourceDetails, setAllResourceDetails] = useState<
    ResourceDetails[]
  >([]);

  const [
    getData,
    { data: resourceDetailsData, loading, error, fetchMore, refetch },
  ] = useGetAllResourceDetailsLazyQuery();

  const getAllResourceDetails = useCallback(
    () =>
      getData({
        variables: {
          page: 1,
          limit: LIMIT,
          sort,
          input,
        },
      }),
    [getData]
  );

  const count = resourceDetailsData?.resourcesSearch?.pageInfo.count ?? 0;
  const pages = resourceDetailsData?.resourcesSearch?.pageInfo?.pages ?? 0;

  useEffect(() => {
    const initialQueryResult =
      resourceDetailsData?.resourcesSearch?.result
        ?.filter((result): result is NonNullable<typeof result> => !!result)
        ?.map(mapResultToResourceDetails) ?? [];

    setAllResourceDetails(initialQueryResult);
    if (!loading && count > initialQueryResult.length) {
      [...Array(pages).keys()].slice(1).forEach((page) => {
        fetchMore?.({
          variables: {
            page: page + 1,
            limit: LIMIT,
            sort,
            input,
          },
        }).then(({ data: fetchedData }) => {
          const queryResult =
            fetchedData?.resourcesSearch?.result
              ?.filter(
                (result): result is NonNullable<typeof result> => !!result
              )
              ?.map(mapResultToResourceDetails) ?? [];

          return setAllResourceDetails((prev) => [...prev, ...queryResult]);
        });
      });
    }
  }, [
    count,
    fetchMore,
    pages,
    resourceDetailsData?.resourcesSearch?.result,
    setAllResourceDetails,
  ]);

  return {
    getAllResourceDetails,
    allResourceDetails,
    loading,
    error,
    count,
    refetch,
  } as const;
}

function mapResultToResourceDetails(result: {
  __typename?: "Resource" | undefined;
  id: string;
  name: string;
  scanned: boolean;
  os?:
    | {
        __typename?: "ResourceOS" | undefined;
        type: OsType;
      }
    | null
    | undefined;
}) {
  return {
    id: result.id,
    name: result.name,
    scanned: result.scanned,
    os: result.os?.type,
  };
}
